import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from './firebase';
import { isCacheStale, recordDataSync } from './cacheService';
import { openIndexedDBWithStores } from './indexedDBUtils';
import { conditionalLog } from './consoleLogger';

/**
 * Local-first Equipment Service for STB Workshop
 * Prioritizes IndexedDB cache with Firestore fallback to minimize reads
 */

// ===== EQUIPMENT DATA OPERATIONS =====

/**
 * Gets all equipment with local-first architecture
 * Uses IndexedDB cache first, falls back to Firestore only when needed
 * @param {boolean} includeArchived - Whether to include archived equipment
 * @returns {Promise<Array>} Array of equipment objects
 */
export const getAllEquipment = async (includeArchived = false) => {
  try {
    // Try to get from IndexedDB cache first but check cache freshness
    const cachedEquipment = await getEquipmentFromCache();
    
    if (cachedEquipment && cachedEquipment.length > 0) {
      // Check if cache is stale
      if (!isCacheStale('equipment')) {
        conditionalLog(`Using cached equipment data (${cachedEquipment.length} items)`);
        
        // Deserialize dates from cached data
        let deserializedEquipment = cachedEquipment.map(item => deserializeDates(item));
        
        // Check if cached data needs population (for legacy cached data without names)
        const needsPopulation = deserializedEquipment.some(item => 
          (item.tipoId && !item.tipo) || (item.marcaId && !item.marca)
        );
        
        if (needsPopulation) {
          conditionalLog('Cached equipment needs population, adding names...');
          try {
            deserializedEquipment = await populateEquipmentWithNames(deserializedEquipment);
            // Update cache with populated data
            await syncEquipmentToCache(deserializedEquipment);
          } catch (error) {
            console.error('Error populating cached equipment:', error);
            // Continue with unpopulated data rather than failing completely
          }
        }
        
        // Filter archived equipment locally if needed
        let filteredEquipment = deserializedEquipment;
        if (!includeArchived) {
          filteredEquipment = deserializedEquipment.filter(item => !item.isArchived);
        }
        
        // Sort by most recent activity (updated or created) - newest first
        filteredEquipment.sort((a, b) => {
          const getMostRecentDate = (item) => {
            const updatedAt = item.updatedAt instanceof Date ? item.updatedAt : new Date(item.updatedAt || 0);
            const createdAt = item.createdAt instanceof Date ? item.createdAt : new Date(item.createdAt || 0);
            return updatedAt > createdAt ? updatedAt : createdAt;
          };
          
          const dateA = getMostRecentDate(a);
          const dateB = getMostRecentDate(b);
          const result = dateB - dateA; // Descending order (most recent first)
          
          // If dates are equal, sort by modelo as secondary criteria
          if (result === 0) {
            const modeloA = (a.modelo || '').toLowerCase();
            const modeloB = (b.modelo || '').toLowerCase();
            return modeloA.localeCompare(modeloB);
          }
          
          return result;
        });
        
        return filteredEquipment;
      } else {
        conditionalLog('Cached equipment is stale, fetching fresh data from Firestore...');
      }
    }
    
    // Cache miss - fetch from Firestore and update cache
    conditionalLog('No cached equipment data found, fetching from Firestore...');
    return await fetchAndCacheEquipment(includeArchived);
    
  } catch (error) {
    console.error('Error getting equipment:', error);
    
    // Fallback to Firestore if IndexedDB fails
    try {
      return await fetchAndCacheEquipment(includeArchived);
    } catch (firestoreError) {
      console.error('Error fetching from Firestore:', firestoreError);
      return [];
    }
  }
};

/**
 * Fetches equipment from Firestore and updates IndexedDB cache
 * @param {boolean} includeArchived - Whether to include archived equipment  
 * @returns {Promise<Array>} Array of equipment objects
 */
const fetchAndCacheEquipment = async (includeArchived = false) => {
  try {
    // Always fetch all equipment and filter locally to include items without isArchived field
    const q = query(collection(db, 'equipment_catalog'));
    
    const querySnapshot = await getDocs(q);
    let equipmentList = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter archived equipment locally (this includes items without isArchived field)
    if (!includeArchived) {
      equipmentList = equipmentList.filter(item => item.isArchived !== true);
    }
    
    // Populate equipment with type and brand names first
    const populatedEquipment = await populateEquipmentWithNames(equipmentList);
    
    // Sort by most recent activity (updated or created) - newest first
    populatedEquipment.sort((a, b) => {
      const getMostRecentDate = (item) => {
        const updatedAt = item.updatedAt?.toDate ? item.updatedAt.toDate() : new Date(item.updatedAt || 0);
        const createdAt = item.createdAt?.toDate ? item.createdAt.toDate() : new Date(item.createdAt || 0);
        return updatedAt > createdAt ? updatedAt : createdAt;
      };
      
      const dateA = getMostRecentDate(a);
      const dateB = getMostRecentDate(b);
      const result = dateB - dateA; // Descending order (most recent first)
      
      // If dates are equal, sort by modelo as secondary criteria
      if (result === 0) {
        const modeloA = (a.modelo || '').toLowerCase();
        const modeloB = (b.modelo || '').toLowerCase();
        return modeloA.localeCompare(modeloB);
      }
      
      return result;
    });
    
    // Update IndexedDB cache with fresh data (with populated names)
    await syncEquipmentToCache(populatedEquipment);
    
    // Record cache sync time
    recordDataSync('equipment');
    
    conditionalLog(`Fetched ${populatedEquipment.length} equipment items from Firestore and updated cache`);
    return populatedEquipment;
    
  } catch (error) {
    console.error('Error fetching equipment from Firestore:', error);
    throw error;
  }
};

/**
 * Gets equipment from IndexedDB cache
 * @returns {Promise<Array|null>} Cached equipment or null if not available
 */
const getEquipmentFromCache = async () => {
  try {
    const db = await openIndexedDBWithStores();
    
    
    if (!db.objectStoreNames.contains('equipment_catalog')) {
      return null;
    }
    
    return new Promise((resolve) => {
      const transaction = db.transaction(['equipment_catalog'], 'readonly');
      const store = transaction.objectStore('equipment_catalog');
      const getAllRequest = store.getAll();
      
      getAllRequest.onsuccess = () => {
        const equipment = getAllRequest.result;
        db.close();
        if (equipment && equipment.length > 0) {
          resolve(equipment);
        } else {
          resolve(null); // Empty cache
        }
      };
      
      getAllRequest.onerror = () => {
        db.close();
        resolve(null);
      };
    });
  } catch (error) {
    console.error('Error getting equipment from cache:', error);
    return null;
  }
};

/**
 * Syncs equipment data to IndexedDB cache
 * @param {Array} equipment - Equipment to cache
 * @returns {Promise<boolean>} Success status
 */
const syncEquipmentToCache = async (equipment) => {
  try {
    const db = await openIndexedDBWithStores();
    
    
    if (!db.objectStoreNames.contains('equipment_catalog')) {
      console.warn('Equipment catalog object store does not exist');
      db.close();
      return false;
    }
    
    return new Promise((resolve) => {
      const transaction = db.transaction(['equipment_catalog'], 'readwrite');
      const store = transaction.objectStore('equipment_catalog');
      
      // Clear existing data and add new data
      store.clear();
      equipment.forEach(item => {
        store.put(item);
      });
      
      transaction.oncomplete = () => {
        conditionalLog(`Cached ${equipment.length} equipment items to IndexedDB`);
        db.close();
        resolve(true);
      };
      
      transaction.onerror = () => {
        console.error('IndexedDB transaction error:', transaction.error);
        db.close();
        resolve(false);
      };
    });
  } catch (error) {
    console.error('Error syncing equipment to cache:', error);
    return false;
  }
};

// ===== EQUIPMENT USAGE CHECKING =====

/**
 * Checks which equipment is used in orders (optimized version)
 * Uses cached orders data to avoid full table scans for each equipment item
 * @param {Array} equipmentIds - Equipment IDs to check
 * @returns {Promise<Set>} Set of equipment IDs that are in use
 */
export const getEquipmentInUse = async (equipmentIds) => {
  try {
    const inUseSet = new Set();
    
    // Try to get orders from IndexedDB cache first
    const cachedOrders = await getOrdersFromCache();
    
    if (cachedOrders && cachedOrders.length > 0) {
      conditionalLog('Using cached orders data to check equipment usage');
      
      // Check which equipment is used in cached orders
      cachedOrders.forEach(order => {
        if (order.equipos && Array.isArray(order.equipos)) {
          order.equipos.forEach(equipo => {
            if (equipo.catalogId && equipmentIds.includes(equipo.catalogId)) {
              inUseSet.add(equipo.catalogId);
            }
          });
        }
      });
      
      return inUseSet;
    }
    
    // Fallback: if no cached orders, do a single query for all orders
    conditionalLog('No cached orders found, querying Firestore for equipment usage check');
    const ordersQuery = query(collection(db, 'orders'));
    const ordersSnapshot = await getDocs(ordersQuery);
    
    ordersSnapshot.docs.forEach(doc => {
      const orderData = doc.data();
      if (orderData.equipos && Array.isArray(orderData.equipos)) {
        orderData.equipos.forEach(equipo => {
          if (equipo.catalogId && equipmentIds.includes(equipo.catalogId)) {
            inUseSet.add(equipo.catalogId);
          }
        });
      }
    });
    
    return inUseSet;
    
  } catch (error) {
    console.error('Error checking equipment usage:', error);
    return new Set(); // Return empty set on error
  }
};

/**
 * Gets orders from IndexedDB cache (helper function)
 * @returns {Promise<Array|null>} Cached orders or null
 */
const getOrdersFromCache = async () => {
  try {
    const request = indexedDB.open('STBWorkshop', 23);
    
    return new Promise((resolve) => {
      request.onerror = () => resolve(null);
      
      request.onsuccess = () => {
        const db = request.result;
        
        if (!db.objectStoreNames.contains('orders')) {
          resolve(null);
          return;
        }
        
        const transaction = db.transaction(['orders'], 'readonly');
        const store = transaction.objectStore('orders');
        const getAllRequest = store.getAll();
        
        getAllRequest.onsuccess = () => {
          resolve(getAllRequest.result || null);
        };
        
        getAllRequest.onerror = () => resolve(null);
      };
    });
  } catch (error) {
    console.error('Error getting orders from cache:', error);
    return null;
  }
};

// ===== SEARCH FUNCTIONS =====

/**
 * Searches equipment locally using IndexedDB cache
 * @param {string} searchTerm - Search term
 * @param {boolean} includeArchived - Whether to include archived equipment
 * @returns {Promise<Array>} Filtered equipment
 */
export const searchEquipmentLocal = async (searchTerm, includeArchived = false) => {
  try {
    if (!searchTerm.trim()) {
      return await getAllEquipment(includeArchived);
    }
    
    const equipment = await getAllEquipment(includeArchived);
    // Get brands for dynamic lookup
    const { getAllBrands } = await import('./brandService');
    const brands = await getAllBrands();
    const searchTermLower = searchTerm.toLowerCase();
    
    return equipment.filter(item => {
      // Search in basic fields
      if ((item.tipo && item.tipo.toLowerCase().includes(searchTermLower)) ||
          (item.modelo && item.modelo.toLowerCase().includes(searchTermLower)) ||
          (item.numeroParte && item.numeroParte.toLowerCase().includes(searchTermLower)) ||
          (item.descripcion && item.descripcion.toLowerCase().includes(searchTermLower))) {
        return true;
      }
      
      // Search in brand name using dynamic lookup
      if (item.marcaId) {
        const brand = brands.find(b => b.id === item.marcaId);
        if (brand && brand.nombre && brand.nombre.toLowerCase().includes(searchTermLower)) {
          return true;
        }
      }
      
      // Search in specifications
      if (item.especificaciones) {
        const specs = Object.values(item.especificaciones);
        if (specs.some(spec => spec && spec.toString().toLowerCase().includes(searchTermLower))) {
          return true;
        }
      }
      
      return false;
    });
  } catch (error) {
    console.error('Error searching equipment locally:', error);
    return [];
  }
};

// ===== UTILITY FUNCTIONS =====

/**
 * Converts serialized date objects back to Date objects
 * Handles Firestore Timestamps that have been serialized to IndexedDB
 * @param {Object} obj - Object that may contain serialized dates
 * @returns {Object} Object with dates converted to Date objects
 */
const deserializeDates = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const result = Array.isArray(obj) ? [] : {};
  
  for (const key in obj) {
    const value = obj[key];
    
    if (value && typeof value === 'object') {
      // Check if this looks like a serialized Firestore Timestamp
      if (value.seconds && typeof value.seconds === 'number') {
        result[key] = new Date(value.seconds * 1000);
      } else if (Array.isArray(value)) {
        result[key] = value.map(item => deserializeDates(item));
      } else {
        result[key] = deserializeDates(value);
      }
    } else {
      result[key] = value;
    }
  }
  
  return result;
};

/**
 * Populates equipment with type and brand names from their IDs
 * @param {Array} equipmentList - Array of equipment objects
 * @returns {Promise<Array>} Equipment array with populated names
 */
const populateEquipmentWithNames = async (equipmentList) => {
  try {
    if (!equipmentList || equipmentList.length === 0) {
      return equipmentList;
    }

    // Collect unique IDs that need to be looked up
    const typeIds = new Set();
    const brandIds = new Set();
    
    equipmentList.forEach(equipment => {
      if (equipment.tipoId) {
        typeIds.add(equipment.tipoId);
      }
      if (equipment.marcaId) {
        brandIds.add(equipment.marcaId);
      }
    });

    // Fetch types and brands data in parallel with error handling
    const [typesData, brandsData] = await Promise.all([
      getTypesLookupData(Array.from(typeIds)).catch(err => {
        console.error('Error fetching types for population:', err);
        return [];
      }),
      getBrandsLookupData(Array.from(brandIds)).catch(err => {
        console.error('Error fetching brands for population:', err);
        return [];
      })
    ]);

    // Create lookup maps for O(1) access
    const typeMap = new Map();
    typesData.forEach(type => typeMap.set(type.id, type.nombre));
    
    const brandMap = new Map();
    brandsData.forEach(brand => brandMap.set(brand.id, brand.nombre));

    // Populate equipment with names
    const populatedEquipment = equipmentList.map(equipment => ({
      ...equipment,
      tipo: typeMap.get(equipment.tipoId) || equipment.tipo || 'Tipo no encontrado',
      marca: brandMap.get(equipment.marcaId) || equipment.marca || 'Marca no encontrada'
    }));

    return populatedEquipment;

  } catch (error) {
    console.error('Error populating equipment with names:', error);
    return equipmentList; // Return original data if population fails
  }
};

/**
 * Gets types data for lookup by IDs - uses direct Firestore access to avoid circular dependency
 * @param {Array} typeIds - Array of type IDs to fetch
 * @returns {Promise<Array>} Array of type objects
 */
const getTypesLookupData = async (typeIds) => {
  if (typeIds.length === 0) return [];
  
  try {
    // Direct Firestore access to avoid circular dependency with typeService
    const { collection, getDocs, query, where } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    
    if (typeIds.length === 1) {
      // For single ID, use where query
      const q = query(collection(db, 'tipos_equipo'), where('__name__', '==', typeIds[0]));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } else {
      // For multiple IDs, get all types and filter (more efficient than multiple queries)
      const q = query(collection(db, 'tipos_equipo'));
      const querySnapshot = await getDocs(q);
      const allTypes = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter to only the types we need
      return allTypes.filter(type => typeIds.includes(type.id));
    }
  } catch (error) {
    console.error('Error fetching types for lookup:', error);
    return [];
  }
};

/**
 * Gets brands data for lookup by IDs - uses direct Firestore access to avoid circular dependency
 * @param {Array} brandIds - Array of brand IDs to fetch
 * @returns {Promise<Array>} Array of brand objects
 */
const getBrandsLookupData = async (brandIds) => {
  if (brandIds.length === 0) return [];
  
  try {
    // Direct Firestore access to avoid circular dependency with brandService
    const { collection, getDocs, query, where } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    
    if (brandIds.length === 1) {
      // For single ID, use where query
      const q = query(collection(db, 'marcas'), where('__name__', '==', brandIds[0]));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } else {
      // For multiple IDs, get all brands and filter
      const q = query(collection(db, 'marcas'));
      const querySnapshot = await getDocs(q);
      const allBrands = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter to only the brands we need
      return allBrands.filter(brand => brandIds.includes(brand.id));
    }
  } catch (error) {
    console.error('Error fetching brands for lookup:', error);
    return [];
  }
};

/**
 * Forces a refresh of equipment data from Firestore
 * Useful when data might have been updated externally
 * @param {boolean} includeArchived - Whether to include archived equipment
 * @returns {Promise<Array>} Fresh equipment data
 */
export const refreshEquipmentData = async (includeArchived = false) => {
  try {
    conditionalLog('Forcing refresh of equipment data from Firestore...');
    return await fetchAndCacheEquipment(includeArchived);
  } catch (error) {
    console.error('Error refreshing equipment data:', error);
    throw error;
  }
};

/**
 * Gets all brands from equipment data (derived from cached equipment)
 * @returns {Promise<Array>} Array of unique brands with colors
 */
export const getBrandsFromEquipment = async () => {
  try {
    const equipment = await getAllEquipment(true); // Include archived to get all brands
    const brandsSet = new Set();
    
    // Note: With ID-only brand storage, this function is deprecated
    // Brands should be fetched from brandService instead
    equipment.forEach(item => {
      if (item.marcaId) {
        // This would require looking up brand names, which defeats the purpose
        // Consider using getAllBrands() from brandService instead
        brandsSet.add(item.marcaId);
      }
    });
    
    // Predefined colors for brand badges
    const brandColors = [
      '#3B82F6', // Blue
      '#EF4444', // Red
      '#10B981', // Green
      '#F59E0B', // Yellow
      '#8B5CF6', // Purple
      '#EC4899', // Pink
      '#06B6D4', // Cyan
      '#84CC16', // Lime
      '#F97316', // Orange
      '#6366F1', // Indigo
      '#14B8A6', // Teal
      '#F43F5E', // Rose
      '#A855F7', // Violet
      '#22C55E', // Emerald
      '#FE69B4', // Hot Pink
      '#4ADE80'  // Light Green
    ];
    
    // Return brands with name as both id and nombre, plus assigned colors
    return Array.from(brandsSet).sort().map((brand, index) => ({
      id: brand, // Use the brand name as ID for direct matching
      nombre: brand,
      color: brandColors[index % brandColors.length] // Cycle through colors
    }));
  } catch (error) {
    console.error('Error getting brands from equipment:', error);
    return [];
  }
};